import { buildBlockedNote } from "@/components/priorities/buildBlockedNote";
import { PriorityStatusTooltip } from "@/components/priorities/PriorityStatusTooltip";
import { PRIORITY_STATUS_LABEL } from "@/components/priorities/priorityCardTokens";
import { Badge } from "@/components/ui/badge";
import { cn, isPriorityOverdue } from "@/lib/utils";
import { PppChips } from "@/components/priorities/PppChips";
import { Priority, PriorityStatus } from "@/types";
import { Zap, ListTodo, CheckCircle2, AlertCircle, PauseCircle, Hourglass, Crown, AlertTriangle, UserRound, type LucideIcon } from "lucide-react";

const statusConfig: Record<PriorityStatus, { label: string; icon: LucideIcon; className: string }> = {
    focus: { label: PRIORITY_STATUS_LABEL.focus, icon: Zap, className: "bg-primary/10 text-primary" },
    queue: { label: PRIORITY_STATUS_LABEL.queue, icon: ListTodo, className: "bg-info/10 text-info" },
    done: { label: PRIORITY_STATUS_LABEL.done, icon: CheckCircle2, className: "bg-success/10 text-success" },
    blocked: { label: PRIORITY_STATUS_LABEL.blocked, icon: AlertCircle, className: "bg-destructive/10 text-destructive" },
    "on-hold": { label: PRIORITY_STATUS_LABEL["on-hold"], icon: PauseCircle, className: "bg-warning/10 text-warning" },
    waiting: { label: PRIORITY_STATUS_LABEL.waiting, icon: Hourglass, className: "bg-amber-500/10 text-amber-600" },
};

interface TeamMemberPriorityCardProps {
    priority: Priority;
    memberPid?: string;
    onClick: () => void;
}

export const TeamMemberPriorityCard = ({ priority, memberPid, onClick }: TeamMemberPriorityCardProps) => {
    const memberAssignment = memberPid
        ? priority.assignments?.find(a => a.assignedUser.pid === memberPid)
        : undefined;
    const effectiveStatus: PriorityStatus = memberAssignment?.status ?? priority.status ?? 'queue';
    const status = statusConfig[effectiveStatus];
    const StatusIcon = status.icon;
    const isOverdue = effectiveStatus !== 'done' && isPriorityOverdue(priority.weekNumber, priority.weekYear);
    const blockedNote = effectiveStatus === 'blocked' ? buildBlockedNote(priority) : null;
    const isBlocked = effectiveStatus === "blocked";
    // Ownership from THIS member's perspective: DRI when their assignment carries the flag, IC
    // otherwise. The DRI row further down names whoever the DRI is; this badge says which role the
    // member whose column this card sits in is playing.
    const isMemberDri = memberAssignment?.isDri === true;
    const icTask = memberAssignment?.icTask?.trim();

    return (
        <div
            onClick={onClick}
            className={cn(
                "h-full cursor-pointer flex flex-col rounded-lg border p-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition-all duration-200",
                isOverdue
                    ? "border-destructive/30 bg-destructive/[0.03] hover:border-destructive/45 hover:shadow-[0_8px_20px_rgba(220,38,38,0.08)]"
                    : "border-border/60 bg-card/95 hover:border-border hover:shadow-[0_8px_20px_rgba(15,23,42,0.08)]",
                isBlocked && "border-destructive/25 bg-destructive/[0.02]",
            )}
        >
            <div className="mb-3.5 flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                    {/* Code & PPP */}
                    <div className="mb-2.5 flex flex-wrap items-center gap-2">
                        <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground/75">
                            {priority.code}
                        </span>
                        {/* Ownership before PPP: DRI/IC is the decision-relevant bit — it earns
                            the first read, the PPP is context. */}
                        {memberAssignment && (
                            <Badge
                                variant="outline"
                                className={cn(
                                    "h-5 gap-1 rounded-md px-2 text-[10px] font-semibold uppercase tracking-[0.14em] shadow-none",
                                    isMemberDri
                                        ? "border-primary/25 bg-primary/10 text-primary"
                                        : "border-border/60 bg-muted/45 text-muted-foreground",
                                )}
                            >
                                {isMemberDri ? <Crown className="h-3 w-3" /> : <UserRound className="h-3 w-3" />}
                                {isMemberDri ? "DRI" : "IC"}
                            </Badge>
                        )}
                        <PppChips ppps={priority.ppps} isCandidate={priority.isCandidate} max={1} />
                    </div>

                    {/* Priority Name */}
                    <h3 className="line-clamp-2 text-[15px] font-semibold leading-5 tracking-[-0.01em] text-foreground">
                        {priority.name}
                    </h3>

                    {/* IC scope — what this member specifically owns on a priority they don't lead */}
                    {!isMemberDri && icTask && (
                        <p className="mt-1 line-clamp-2 text-[13px] leading-snug text-muted-foreground" title={icTask}>
                            {icTask}
                        </p>
                    )}
                </div>

                {/* Status Badge */}
                <PriorityStatusTooltip status={effectiveStatus} className="flex-shrink-0">
                    <Badge variant="outline" className={cn("h-6 flex-shrink-0 gap-1.5 rounded-md border-transparent px-2 text-[10px] font-semibold uppercase tracking-[0.16em]", status.className)}>
                        <StatusIcon className="h-3 w-3" />
                        {status.label}
                    </Badge>
                </PriorityStatusTooltip>
            </div>

            {/* People Section */}
            <div className="mt-auto flex flex-col">
                {/* DRI */}
                {priority.dri && (
                    <div className="flex items-center gap-2 rounded-md px-0.5 py-0.5">
                        <Crown className="h-3.5 w-3.5 flex-shrink-0 text-primary/65" />
                        <div className="h-6 w-6 flex-shrink-0 overflow-hidden rounded-full border border-primary/20 bg-primary/10 text-[10px] font-bold flex items-center justify-center">
                            {priority.dri.avatarUrl ? (
                                <img src={priority.dri.avatarUrl} alt={priority.dri.fullName} className="h-full w-full object-cover" />
                            ) : (
                                <span className="text-primary">{priority.dri.fullName.charAt(0).toUpperCase()}</span>
                            )}
                        </div>
                        <span className="truncate text-[13px] font-medium leading-none text-foreground/85">
                            {priority.dri.fullName}
                        </span>
                        <span className="ml-auto text-[9px] font-semibold uppercase tracking-[0.18em] text-primary/55">DRI</span>
                    </div>
                )}

                {/* Blocker (when not already covered by blocked-status banner) */}
                {priority.blocker && !blockedNote && (
                    <div className="-mx-0.5 flex items-center gap-2 rounded-md border border-destructive/15 bg-destructive/[0.045] px-2.5 py-2">
                        <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 text-destructive/80" />
                        <div className="h-6 w-6 flex-shrink-0 overflow-hidden rounded-full border border-destructive/25 bg-destructive/10 text-[10px] font-bold flex items-center justify-center">
                            {priority.blocker.avatarUrl ? (
                                <img src={priority.blocker.avatarUrl} alt={priority.blocker.fullName} className="h-full w-full object-cover" />
                            ) : (
                                <span className="text-destructive">{priority.blocker.fullName.charAt(0).toUpperCase()}</span>
                            )}
                        </div>
                        <span className="truncate text-[13px] font-medium leading-none text-destructive/90">
                            {priority.blocker.fullName}
                        </span>
                        <span className="ml-auto text-[9px] font-semibold uppercase tracking-[0.18em] text-destructive/65">Blocked</span>
                    </div>
                )}
            </div>

            {blockedNote ? (
                <div className="mt-3 border-t border-red-200/80 bg-red-50/90 -mx-4 -mb-4 rounded-b-lg px-4 py-2.5 dark:border-red-900/50 dark:bg-red-950/35">
                    <p className="text-xs leading-snug text-red-800 dark:text-red-200">{blockedNote}</p>
                </div>
            ) : null}
        </div>
    );
};
